import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify';
import { getStudentById, getStudentSubjectsById, getStudentWorkSummaryHistory, getStudentModulesBySubject, getStudentWorkWeeksList } from '@app/services/student';
import moment from 'moment';
import { Button } from '@app/components';
import { useSelector } from 'react-redux';
import Select from "react-select";
import StudentDetailsCard from './StudentDetailsCard';
import { encodeId } from '@app/helpers/base64url';
import PrintWorkActions from './PrintWorkActions';

const StudentWorkSummaryHistory = () => {

    const location = useLocation();
    const id = (location.state as { id?: string })?.id;
    const navigate = useNavigate()
    let obj: any = {}
    const [isLoading, setLoading] = useState(false);
    const [weeklyWork, setWeeklyWork] = useState([]);
    const [studentWork, setStudentWork] = useState([]);
    const [activeWeek, setActiveWeek] = useState(1);
    const [globalWeekCounter, setGlobalWeekCounter] = useState(0);
    const [totalWeek, setTotalWeek] = useState(0);
    const [filteredWeeks, setFilteredWeeks] = useState<number[]>([]); // empty = show all
    const [streamPerWeek, setStreamPerWeek] = useState(0);
    const [student, setStudent] = useState(obj);
    const canAccess = useSelector((state: any) => state.auth.permissions.student);
    const studentWorkStore = useSelector((state: any) => state.studentWork);
    const [search, setSearch] = useState({
        subject_id: studentWorkStore?.subject?.id ?? 0,
        module_id: '',
        week_number: ''
    });
    const [subjects, setSubjects] = useState([]);
    const [modules, setModules] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [allWeeks, setAllWeeks] = useState([]);
    const [weeksRaw, setWeeksRaw] = useState<any[]>([]);
    const [stream1, setStream1] = useState('');
    const [stream2, setStream2] = useState('');
    const [stream3, setStream3] = useState('');
    const [stream4, setStream4] = useState('');
    const [selectedModulePathway, setSelectedModulePathway] = useState('');

    useEffect(() => {
        if (!id) {
            toast.error('Student ID not found');
            navigate(-1);
            return;
        }
        getStudentDetailsById()
        getStudentAllActiveSubjects(id)
        // Fetch modules for initial subject (including 0 for Mix and Match)
        if (search.subject_id !== undefined && search.subject_id !== null && search.subject_id !== '') {
            fetchModulesBySubject(id, search.subject_id)
        }
        fetchWeeksList(id, search.subject_id)
        getStudentWorkSummaryHistoryData(id, search.subject_id, search.week_number, search.module_id)
    },[]);

    const getStudentDetailsById = async () => {
        try {
            setLoading(true)
            const resp = await getStudentById(parseInt(id));
            if (resp.success === true) {
                setLoading(false)
                setStudent(resp?.collections)
            } else {
                setLoading(false)
                toast.error(resp?.errors[0]?.msg)
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.response?.message || 'Internal server error');
        }
    };

    const getStudentAllActiveSubjects = async (id) => {
        try {
            setLoading(true)
            const resp = await getStudentSubjectsById(id)
            if (resp.success === true) {
                setLoading(false)
				setSubjects([{id: 0, name: 'Mix and Match'}].concat(resp?.collections))
            } else {
				setLoading(false)
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
			setLoading(false)
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getStudentWorkSummaryHistoryData = async (student_id, subject_id, week = '', module_id = '') => {
        try {
            setLoading(true)
            const payload = {
                student_id: parseInt(student_id),
                subject_id: parseInt(subject_id),
                week: week,
                module_id: module_id
            };
            
            const resp = await getStudentWorkSummaryHistory(payload);
            
            if (resp.success === true) {
                setLoading(false)
                const data = resp?.collections;
                
                setStudentWork(data.summary)
                setStreamPerWeek(data.modules_per_week)
                setGlobalWeekCounter(data.global_week_counter)
                setTotalWeek(data.total_week || data.global_week_counter)
                
                // Set current week as default if not already set
                if (!week && data.current_week) {
                    setSearch(prev => ({ ...prev, week_number: data.current_week.toString() }));
                    setActiveWeek(data.current_week);
                }
                
                if(data.summary.length > 0){
                    setWeeklyWork(data.summary[0])
                    if(data.modules_per_week >= 1)
                        setStream1(data.module1 ?? '')
                    if(data.modules_per_week >= 2)
                        setStream2(data.module2 ?? '')
                    if(data.modules_per_week >= 3)
                        setStream3(data.module3 ?? '')
                    if(data.modules_per_week >= 4 )
                        setStream4(data.module4 ?? '')
                    
                    // If module is selected, find and set the pathway name
                    if (module_id) {
                        // Find which module key this module_id belongs to by checking the summary data
                        const firstDayWithModules = data.summary[0]?.find(day => day.modules && day.modules.length > 0);
                        if (firstDayWithModules) {
                            const moduleData = firstDayWithModules.modules.find(m => m.module_id == module_id);
                            if (moduleData) {
                                // Get the pathway name from the data object using the module key
                                const pathwayName = data[moduleData.module];
                                setSelectedModulePathway(pathwayName || '');
                            }
                        }
                    } else {
                        setSelectedModulePathway('');
                    }
                } else {
                    setWeeklyWork([])
                    setSelectedModulePathway('');
                }
            } else {
                setLoading(false)
                toast.error(resp?.errors[0]?.msg)
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.message || 'Internal server error');
        }
    };

    const fetchModulesBySubject = async (student_id, subject_id) => {
        try {
            const payload = {
                student_id: parseInt(student_id),
                subject_id: parseInt(subject_id)
            };
            
            const resp = await getStudentModulesBySubject(payload);
            
            if (resp.success === true) {
                const modulesData = resp?.collections || [];
                const modulesArr = modulesData.map(m => ({
                    id: m.module_id.toString(),
                    name: `${m.module_name} [Worksheets: ${m.worksheet_from}-${m.worksheet_to}]`,
                    available_weeks: m.available_weeks || []
                }));
                setModules(modulesArr);
            }
        } catch (error: any) {
            console.error('Error fetching modules:', error);
        }
    };

    const fetchWeeksList = async (student_id, subject_id) => {
        try {
            const resp = await getStudentWorkWeeksList({ student_id: parseInt(student_id), subject_id: parseInt(subject_id) });
            if (resp?.success === true) {
                const rawWeeks = resp.collections || [];
                const weeksArr = rawWeeks.map((w: any) => ({
                    id: w.week_number,
                    name: `${w.week_label} - ${moment(w.start_date).format('DD/MM/YYYY')}`
                }));
                setWeeksRaw(rawWeeks);
                setAllWeeks(weeksArr);
                setWeeks(weeksArr);
            }
        } catch (error: any) {
            console.error('Error fetching weeks list:', error);
        }
    };

    const handleSubjectChange = async (subject_id) => {
        setSearch({ ...search, subject_id: subject_id, module_id: '', week_number: '' })
        setFilteredWeeks([]);
        await fetchModulesBySubject(id, subject_id)
        fetchWeeksList(id, subject_id)
        getStudentWorkSummaryHistoryData(id, subject_id, '', '')
    };

    const handleModuleChange = (selectedOption) => {
        const module_id = selectedOption ? selectedOption.value : '';
        setSearch({ ...search, module_id: module_id, week_number: '' })

        if (selectedOption && selectedOption.available_weeks?.length > 0) {
            const availableWeeks: number[] = selectedOption.available_weeks;
            setFilteredWeeks(availableWeeks);
            const filtered = allWeeks.filter((w: any) => availableWeeks.includes(w.id));
            setWeeks(filtered);

            const currentWeek = search.week_number ? parseInt(search.week_number) : activeWeek;
            const targetWeek = availableWeeks.includes(currentWeek)
                ? currentWeek.toString()
                : availableWeeks[0].toString();

            setSearch({ ...search, module_id: module_id, week_number: targetWeek });
            getStudentWorkSummaryHistoryData(id, search.subject_id, targetWeek, module_id);
            return;
        } else if (!selectedOption) {
            setFilteredWeeks([]);
            setWeeks(allWeeks);
        }

        getStudentWorkSummaryHistoryData(id, search.subject_id, '', module_id)
    };

    const handleWeekChange = (selectedOption) => {
        const week_number = selectedOption ? selectedOption.value : '';
        setSearch({ ...search, week_number: week_number })
        getStudentWorkSummaryHistoryData(id, search.subject_id, week_number, search.module_id)
    };

    const getPaginationContent = () => {
        let content = [];
        const currentWeek = search.week_number ? parseInt(search.week_number) : activeWeek;

        // Use filtered weeks if module is selected, otherwise all weeks
        const weekNumbers = filteredWeeks.length > 0
            ? filteredWeeks
            : Array.from({ length: totalWeek }, (_, i) => i + 1);

        if (weekNumbers.length === 0) return content;

        const currentIndex = weekNumbers.indexOf(currentWeek);
        const maxVisible = 5;
        let startIdx = Math.max(0, currentIndex - Math.floor(maxVisible / 2));
        let endIdx = Math.min(weekNumbers.length - 1, startIdx + maxVisible - 1);
        if (endIdx - startIdx < maxVisible - 1) startIdx = Math.max(0, endIdx - maxVisible + 1);

        // Previous button
        if (currentIndex > 0) {
            content.push(
                <li className="page-item" key="prev">
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[currentIndex - 1])}>«</a>
                </li>
            );
        }

        // First week if not visible
        if (startIdx > 0) {
            content.push(
                <li className="page-item" key={weekNumbers[0]}>
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[0])}>Week {weekNumbers[0]}</a>
                </li>
            );
            if (startIdx > 1) content.push(<li className="page-item disabled" key="dots1"><span className="page-link">...</span></li>);
        }

        // Visible weeks
        for (let i = startIdx; i <= endIdx; i++) {
            const w = weekNumbers[i];
            content.push(
                <li className="page-item" key={w}>
                    <a className="page-link"
                       style={{backgroundColor: currentWeek == w ? '#dee2e6' : '#fff'}}
                       href="#"
                       onClick={(e) => changeWeek(e, w)}>
                        Week {w}
                    </a>
                </li>
            );
        }

        // Last week if not visible
        if (endIdx < weekNumbers.length - 1) {
            if (endIdx < weekNumbers.length - 2) content.push(<li className="page-item disabled" key="dots2"><span className="page-link">...</span></li>);
            content.push(
                <li className="page-item" key={weekNumbers[weekNumbers.length - 1]}>
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[weekNumbers.length - 1])}>Week {weekNumbers[weekNumbers.length - 1]}</a>
                </li>
            );
        }

        // Next button
        if (currentIndex < weekNumbers.length - 1) {
            content.push(
                <li className="page-item" key="next">
                    <a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[currentIndex + 1])}>»</a>
                </li>
            );
        }

        return content;
    };

    const changeWeek = (e, week) => {
        e.preventDefault();
        setActiveWeek(week);
        setSearch({ ...search, week_number: week.toString() });
        getStudentWorkSummaryHistoryData(id, search.subject_id, week.toString(), search.module_id);
    }

    // Build the printable weeks from the weeks-list API (each week includes its dates),
    // so the print dropdown supports selecting any/multiple weeks without extra calls.
    const printWeeks = (weeksRaw || []).map((w: any) => ({
        id: w.week_number,
        name: `${w.week_label} - ${moment(w.start_date).format('DD/MM/YYYY')}`,
        week_number: w.week_number,
        days: (w.dates || []).map((d: string) => ({ date: d }))
    }));

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12 text-center">
                        <span style={{fontSize: '1.5rem', fontWeight: 'bold'}}>Student Work Summary History</span>
                    </div>
                    <div className="col-md-12">
                        <StudentDetailsCard student={student} />
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Work Summary History {studentWork.length == 0 ? '' : `- Week ${search.week_number || activeWeek}`}</h3>
                            </div>
                            <div className="card-body">
                                { (canAccess?.['print-worksheets'] || canAccess?.['print-handbooks'] || canAccess?.['print-weeks-summary']) && (
                                    <PrintWorkActions
                                        key={`print-history-${search.subject_id}`}
                                        studId={id}
                                        student={student}
                                        subjectId={Number(search.subject_id)}
                                        weeks={printWeeks}
                                        canAccess={canAccess}
                                        isLoading={isLoading}
                                    />
                                )}
                                <div className="row mb-3">
                                    <div className="col-sm-4">
                                        <label>Subject</label>
                                        <select
                                            className="form-control"
                                            name="subject_id"
                                            onChange={(e) => handleSubjectChange(e.target.value)}
                                            value={search.subject_id}
                                        >
                                            {subjects.length > 0 && subjects.map((obj) => (
                                                <option key={obj.id} value={obj.id}>
                                                    {obj.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-sm-4">
                                        <label>Module</label>
                                        <Select
                                            name="module_id"
                                            placeholder="All Modules"
                                            options={modules.map(m => ({ value: m.id, label: m.name, available_weeks: m.available_weeks }))}
                                            onChange={handleModuleChange}
                                            value={search.module_id ? modules.map(m => ({ value: m.id, label: m.name, available_weeks: m.available_weeks })).find(opt => opt.value === search.module_id) : null}
                                            isLoading={isLoading}
                                            isClearable={true}
                                        />
                                    </div>
                                    <div className="col-sm-4">
                                        <label>Week</label>
                                        <Select
                                            name="week_number"
                                            placeholder="Select week"
                                            options={weeks.map(w => ({ value: w.id.toString(), label: w.name }))}
                                            onChange={handleWeekChange}
                                            value={search.week_number ? weeks.map(w => ({ value: w.id.toString(), label: w.name })).find(opt => opt.value === search.week_number) : null}
                                            isLoading={isLoading}
                                            isClearable={false}
                                        />
                                    </div>
                                </div>
                                {isLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                <table className="table table-bordered">
                                    { weeklyWork.length > 0 && (
                                    <thead>
                                        <tr>
                                            <th style={{ width: 10 }}>#</th>
                                            <th>Date</th>
                                            { streamPerWeek >= 1 && !search.module_id && (
                                                <th>{ stream1 }</th>
                                            )}
                                            { streamPerWeek >= 2 && !search.module_id && (
                                                <th>{ stream2 }</th>
                                            )}
                                            { streamPerWeek >= 3 && !search.module_id && (
                                                <th>{ stream3 }</th>
                                            )}
                                            { streamPerWeek >= 4 && !search.module_id && (
                                                <th>{ stream4 }</th>
                                            )}
                                            { search.module_id && (
                                                <th>{ selectedModulePathway }</th>
                                            )}
                                        </tr>
                                    </thead>
                                    )}
                                    <tbody>
                                        {weeklyWork.length > 0 && weeklyWork.map((obj, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}.</td>
                                                <td>{ moment(obj.date).format('DD/MM/YYYY, dddd') }</td>
                                                { !search.module_id && streamPerWeek >= 1 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module1'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                                { !search.module_id && streamPerWeek >= 2 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module2'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                                { !search.module_id && streamPerWeek >= 3 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module3'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                                { !search.module_id && streamPerWeek >= 4 && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module == 'module4'), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                                { search.module_id && (
                                                    <td>{ obj?.modules !== null ? ( Array.prototype.map.call(obj?.modules.filter(mod => mod.module_id == search.module_id), function(module) { return module?.name + '-' + module?.worksheet_number }) ).join(', ') : '---'}</td>
                                                )}
                                            </tr>
                                        ))}
                                        {weeklyWork.length == 0 && (
                                            <tr>
                                                <td className='text-center' colSpan={6}>There are no work summary to display</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                )}

                            </div>
                            <div className="card-footer">
                                <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                                    {/* ── Action buttons ── */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        <Button className="btn btn-default btn-sm" onClick={() => navigate(-1)}>
                                            <i className="fas fa-times mr-1" />Close
                                        </Button>
                                        { student?.status == 'active' && canAccess?.['set-work'] && (
                                            <Link className="btn btn-default btn-sm" to={`/student/set-work/${encodeId(id)}`} role="button">
                                                <i className="fas fa-network-wired mr-1" />Set Student Work
                                            </Link>
                                        )}
                                        { canAccess?.['work-summary'] && (
                                            <Link className="btn btn-default btn-sm" to="/student/work-summary" state={{ id: id }} role="button">
                                                <i className="fas fa-book-reader mr-1" />Current Student Work
                                            </Link>
                                        )}
                                        { canAccess?.['work-summary'] && (
                                            <Link className="btn btn-default btn-sm" to="/student/module-assignment-list" state={{ id: id }} role="button">
                                                <i className="fas fa-list mr-1" />Module Assignment List
                                            </Link>
                                        )}
                                    </div>
                                    {/* ── Week pagination ── */}
                                    {!isLoading && (
                                        <ul className="pagination pagination-sm m-0">
                                            { getPaginationContent() }
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </section>
    </div>
  )
}

export default StudentWorkSummaryHistory;
